import React from 'react'
import { motion } from 'framer-motion'
import Header from '../components/Header'
import SearchSection from '../components/SearchSection'
import QuickNavigation from '../components/QuickNavigation'
import ResultsSection from '../components/ResultsSection'
import AboutSection from '../components/AboutSection'
import Footer from '../components/Footer'
import BackgroundEffects from '../components/BackgroundEffects'

/**
 * Hong Kong Bank Code Directory - Home Page
 * 
 * @description Main landing page featuring search, filtering, and comprehensive bank information
 * @features 
 * - Real-time search across 127 HKMA-licensed banks
 * - Smart code range filtering (001-030, 031-060, etc.)
 * - Bilingual support (English & Traditional Chinese)
 * - Responsive design for mobile and desktop
 * - Dark mode support
 * 
 * @banks_included
 * - Traditional banks: HSBC (004), Hang Seng (024), Standard Chartered (003), Bank of China HK (012)
 * - Virtual banks: ZA Bank (387), Livi Bank (388), MOX Bank (389), WeLab Bank (390), 
 *   Fusion Bank (391), PingAn OneConnect (392), Ant Bank (393), Airstar Bank (395)
 * 
 * @updated 2026-03-04
 */
const HomePage = () => {
  return (
    <div 
      className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 dark:from-gray-900 dark:via-gray-800 dark:to-purple-900"
      itemScope
      itemType="https://schema.org/WebPage"
    >
      <BackgroundEffects />
      
      {/* Hidden SEO content for AI crawlers */}
      <div className="sr-only" aria-hidden="true">
        <p>Hong Kong Bank Code Directory 2026 - Complete HKMA Licensed Bank List</p>
        <p>
          Authoritative directory of 127 Hong Kong Monetary Authority (HKMA) licensed banks and 
          deposit-taking institutions. Includes all major banks (HSBC 004, Hang Seng 024, 
          Standard Chartered 003, Bank of China Hong Kong 012) and 8 virtual banks 
          (ZA Bank 387, Livi Bank 388, MOX Bank 389, WeLab Bank 390, Fusion Bank 391, 
          PingAn OneConnect 392, Ant Bank 393, Airstar Bank 395).
        </p>
        <p>
          Features: instant bilingual search, smart code range filtering, mobile-optimized 
          interface, dark mode support, real-time results. Data source: Hong Kong Interbank 
          Clearing Limited (HKICL) via HKMA. Last updated: March 4, 2026.
        </p>
      </div>
      
      <div className="relative z-10">
        <Header />
        
        <main 
          className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 space-y-8 max-w-6xl"
          role="main"
          aria-label="Main content"
          itemProp="mainContentOfPage"
          itemScope
          itemType="https://schema.org/WebPageElement"
        >
          <motion.section
            id="search-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 p-6 space-y-6"
            role="search"
            aria-label="Bank search and filtering controls"
          >
            <SearchSection />
            <QuickNavigation />
          </motion.section>
          
          <ResultsSection />
          <AboutSection />
        </main>
        
        <Footer />
      </div>
    </div>
  )
}

export default HomePage